import { useTranslation } from 'react-i18next';

import LogoIcon from '@/component-library/primitives/Icons/LogoIcon/LogoIcon';

type AuthModalWidth = 'small' | 'medium' | 'large';

interface AuthLayoutProps {
  children: React.ReactNode;
  title?: string;
  content?: string;
  titleClass?: string;
  contentClass?: string;
  variant?: 'one-column' | 'two-column';
  modalWidth?: AuthModalWidth;
}

const AuthLayout: React.FC<AuthLayoutProps> = ({
  children,
  title,
  content,
  titleClass,
  contentClass,
  variant = 'one-column',
  modalWidth,
}) => {
  const { t } = useTranslation();

  const tailwindModalWidth = ((modalWidth?: AuthModalWidth) => {
    switch (modalWidth) {
      case 'small':
        return 'max-w-md';
      case 'medium':
        return 'max-w-xl';
      case 'large':
        return 'max-w-3xl';

      default:
        return 'max-w-md';
    }
  })(modalWidth);

  return (
    <>
      {variant === 'one-column' ? (
        <div className="min-h-screen bg-custom-silver text-custom-darkSilver flex items-center justify-center">
          <div className={`w-full relative z-10 px-6 ${tailwindModalWidth}`}>
            <div className="mb-8 text-center">
              <div className="flex justify-center mb-10">
                <LogoIcon withText width="200" />
              </div>
            </div>

            <div className="bg-white backdrop-blur-sm rounded-xl p-8 shadow-md">
              {(title || content) && (
                <div className="mb-10 text-center flex flex-col gap-2">
                  {title && <h3 className={`text-custom-black text-xl font-semibold ${titleClass || ''}`}>{title}</h3>}
                  {content && (
                    <p className={`text-custom-darkGray text-sm leading-1 ${contentClass || ''}`}>{content}</p>
                  )}
                </div>
              )}

              {children}
            </div>

            <footer className="bg-transparent p-0 mt-6 border-0 text-custom-darkSilver text-center text-xs">
              {t('AuthLayout.footer')}
            </footer>
          </div>
        </div>
      ) : (
        <div className="flex flex-col sm:flex-row bg-custom-darkSilver justify-center min-h-screen">
          {/* Left Column */}
          <div className="min-h-screen w-full sm:w-[40%] bg-custom-silver text-custom-darkSilver flex items-center justify-center">
            <div className={`w-full relative z-10 px-6 ${tailwindModalWidth}`}>
              {' '}
              <div className="mb-8 text-center">
                <div className="flex justify-center mb-10">
                  <LogoIcon withText width="200" />
                </div>
              </div>
              <div className="bg-white backdrop-blur-sm rounded-xl p-8 shadow-md">
                {(title || content) && (
                  <div className="mb-10 text-center flex flex-col gap-2">
                    {title && (
                      <h3 className={`text-custom-black text-xl font-semibold ${titleClass || ''}`}>{title}</h3>
                    )}
                    {content && (
                      <p className={`text-custom-darkGray text-sm leading-1 ${contentClass || ''}`}>{content}</p>
                    )}
                  </div>
                )}

                {children}
              </div>
              <footer className="bg-transparent p-0 mt-6 border-0 text-custom-darkSilver text-center text-xs">
                {t('AuthLayout.footer')}
              </footer>
            </div>
          </div>

          {/* Right Column */}
          <div className="hidden sm:flex w-[60%] bg-cover bg-center">
            <div className="h-full w-full"></div>
          </div>
        </div>
      )}
    </>
  );
};

export default AuthLayout;
